import { api } from './client'
import { downloadExportFile, withFormat } from './exports'

/**
 * F-069 / TASK-0323 — export del commercialista (REQ-191-001) e chiusura del periodo con checklist
 * (REQ-181-001). Solo CSV; la checklist è a conferma manuale (riconciliazioni e imposte non esistono
 * ancora nel sistema).
 */

export interface ChecklistItem {
  code: string
  label: string
  checked: boolean
  checkedByUserId: number | null
  checkedAt: string | null
  note: string | null
  nonApplicabile: boolean
}

export interface AccountingPeriod {
  year: number
  month: number
  status: 'Aperto' | 'Chiuso'
  closedAt: string | null
  closedByUserId: number | null
  reopenedAt: string | null
  reopenedByUserId: number | null
  reopenReason: string | null
  pendingCount: number
  checklist: ChecklistItem[]
}

export const EXPORT_DATASETS: { id: string; label: string; dated: boolean }[] = [
  { id: 'expenses', label: 'Spese', dated: true },
  { id: 'invoices', label: 'Fatture', dated: true },
  { id: 'partner-movements', label: 'Movimenti dei soci', dated: true },
  { id: 'suppliers', label: 'Fornitori', dated: false },
  { id: 'accounts', label: 'Conti', dated: false },
  { id: 'categories', label: 'Categorie di spesa', dated: false },
  { id: 'cost-centres', label: 'Centri di costo', dated: false },
  // F-092: movimenti bancari, riconciliazioni, scadenze e budget (senza IBAN, senza note).
  { id: 'movements', label: 'Movimenti bancari', dated: true },
  { id: 'reconciliations', label: 'Riconciliazioni', dated: true },
  { id: 'scheduled-payments', label: 'Scadenze di pagamento', dated: true },
  { id: 'budgets', label: 'Budget di costo', dated: true },
]

export const accountantApi = {
  periods: (year: number) => api<{ year: number; items: AccountingPeriod[] }>(`/api/finance/periods?year=${year}`),

  setItem: (year: number, month: number, code: string, body: { checked: boolean; note?: string | undefined; nonApplicabile?: boolean | undefined }) =>
    api<AccountingPeriod>(`/api/finance/periods/${year}/${month}/checklist/${encodeURIComponent(code)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  close: (year: number, month: number) =>
    api<AccountingPeriod>(`/api/finance/periods/${year}/${month}/close`, { method: 'POST', body: '{}' }),

  /** Solo Admin: il motivo è obbligatorio. */
  reopen: (year: number, month: number, reason: string) =>
    api<AccountingPeriod>(`/api/finance/periods/${year}/${month}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  exportCsv: (dataset: string, from?: string, to?: string, format: 'csv' | 'xlsx' | 'pdf' = 'csv') => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    const qs = q.toString()
    // F-117: con l'approvazione richiesta il server risponde 428 e questa chiamata lancia ExportApprovalRequiredError.
    return downloadExportFile(withFormat(`/api/finance/exports/${dataset}${qs ? `?${qs}` : ''}`, format))
  },

  /** F-140 (REQ-191-001): l'elenco dei documenti dell'area finanza (solo metadati), in CSV o Excel. */
  exportDocuments: (from?: string, to?: string, format: 'csv' | 'xlsx' | 'pdf' = 'csv') => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    const qs = q.toString()
    return downloadExportFile(withFormat(`/api/finance/documents/export${qs ? `?${qs}` : ''}`, format))
  },
}
